/**
 * Planbaarheid-veld bij de medewerker (Epic D-1 — Fundament, Story D-1.4)
 *
 * UX §5.3 (herzien 2026-08-04), bindend: planbaarheid is GEEN apart
 * beheerscherm en geen derde item onder "Lijsten beheren" — het is een veld
 * bij de medewerker. Dit component wordt op het medewerkerdetail gerenderd.
 *
 * Het veld toont de huidige stand, de reden, en of die stand automatisch of
 * handmatig tot stand kwam (AC 8). Staat iemand automatisch buiten de
 * verdeling wegens langdurig verzuim, dan meldt het veld dat dit vanzelf gaat
 * en bij terugkeer vanzelf herstelt — die stand is hier bewust NIET op te
 * heffen (AC 5: de twee regels werken onafhankelijk). De handmatige stand is
 * wél te zetten (reden verplicht — zonder reden is opslaan niet mogelijk) en
 * op te heffen.
 *
 * Self-gated op `hasRole(['admin','hr'])` — zelfde patroon als
 * `LijstenBeherenMenu`: schrijven vereist die rol server-side, dus het veld
 * verschijnt alleen voor wie er iets mee kan.
 */

// Bewust GEEN useToast: het medewerkerdetail wordt niet overal binnen een
// ToastProvider gerenderd (o.a. de bestaande EmployeeDetailPage-testsuites) —
// fout-feedback is hier een inline melding, zoals de andere secties op deze
// pagina dat ook doen.
import { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useClearPoolOverride, usePoolStatus, useSetPoolOverride } from '../hooks/usePlanningPool';

/** Zelfde Europe/Amsterdam-"vandaag"-conventie als de backend (`getTodayIso`). */
function getTodayIso(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date());
}

export function PlanbaarheidVeld({ employeeId }: { employeeId: number }) {
  const { hasRole } = useAuth();
  const magBeheren = hasRole(['admin', 'hr']);
  const today = getTodayIso();
  // De hook-aanroep is onvoorwaardelijk (rules of hooks); de query zelf staat
  // uit voor wie het veld nooit ziet — anders vuurt elk medewerkerdetail een
  // aanroep af die voor niet-beheerders op een 403 stukloopt.
  const { data, isLoading, isError } = usePoolStatus(today, magBeheren);
  const setOverride = useSetPoolOverride();
  const clearOverride = useClearPoolOverride();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [foutmelding, setFoutmelding] = useState<string | null>(null);

  if (!magBeheren) {
    return null;
  }

  const status = data?.find((entry) => entry.employeeId === employeeId);

  async function handleSave() {
    if (!reason.trim()) return;
    setFoutmelding(null);
    try {
      await setOverride.mutateAsync({
        employeeId,
        input: { isPlanbaar: false, reason: reason.trim() },
      });
      setIsFormOpen(false);
      setReason('');
    } catch {
      setFoutmelding('Opslaan is niet gelukt; probeer het opnieuw.');
    }
  }

  async function handleClear() {
    setFoutmelding(null);
    try {
      await clearOverride.mutateAsync(employeeId);
    } catch {
      setFoutmelding('Opheffen is niet gelukt; probeer het opnieuw.');
    }
  }

  return (
    <div
      data-testid="planbaarheid-veld"
      className="bg-white shadow-sm border border-gray-200 rounded-lg p-6"
    >
      <h2 className="text-lg font-medium text-gray-900 mb-1">Planbaarheid</h2>
      <p className="text-sm text-gray-500 mb-4">
        Bepaalt of deze medewerker meedoet in de automatische dagplanning.
      </p>

      {isLoading && <p className="text-sm text-gray-500">Laden…</p>}
      {isError && (
        <p className="text-sm text-[#D64545]">
          De planbaarheid kon niet worden geladen; probeer het later opnieuw.
        </p>
      )}

      {foutmelding && (
        <p data-testid="planbaarheid-foutmelding" className="mb-3 text-sm text-[#D64545]">
          {foutmelding}
        </p>
      )}

      {!isLoading && !isError && !status && (
        // Review-bevinding D-1.4: zonder deze melding blijft alleen de kop
        // over wanneer de medewerker niet in de poolrespons zit — een stille
        // lege kaart waar een uitleg hoort.
        <p className="text-sm text-gray-500">
          Voor deze medewerker zijn geen planbaarheidsgegevens beschikbaar.
        </p>
      )}

      {!isLoading && !isError && status && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span
              data-testid="planbaarheid-stand"
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                status.isPlanbaar ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {status.isPlanbaar ? 'Planbaar' : 'Niet planbaar'}
            </span>
            {status.source && (
              <span data-testid="planbaarheid-bron" className="text-xs text-gray-500">
                {status.source === 'automatisch' ? 'Automatisch' : 'Handmatig'}
              </span>
            )}
          </div>

          {status.autoNietPlanbaar && (
            // AC 8: de automatische stand is hier NIET op te heffen — geen
            // knop, alleen de uitleg dat dit vanzelf gaat en vanzelf herstelt.
            <p
              data-testid="planbaarheid-automatisch-melding"
              className="text-sm text-gray-700 rounded-md bg-[#F3F4F6] px-3 py-2"
            >
              Deze medewerker valt automatisch buiten de verdeling door langdurig verzuim. Dit gaat
              vanzelf en herstelt vanzelf bij terugkeer.
            </p>
          )}

          {status.override ? (
            <div className="rounded-md border border-[#E2E8F0] px-3 py-2">
              <p className="text-sm text-gray-900">
                Handmatige stand:{' '}
                <span className="font-medium">
                  {status.override.isPlanbaar ? 'planbaar' : 'niet planbaar'}
                </span>
              </p>
              <p data-testid="planbaarheid-reden" className="text-sm text-gray-700">
                Reden: {status.override.reason}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Gezet op {new Date(status.override.setAt).toLocaleDateString('nl-NL')}
              </p>
              <button
                type="button"
                data-testid="planbaarheid-opheffen-button"
                onClick={handleClear}
                disabled={clearOverride.isPending}
                className="mt-2 text-sm font-medium text-[#54949E] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                Handmatige stand opheffen
              </button>
            </div>
          ) : isFormOpen ? (
            <div className="rounded-md border border-[#E2E8F0] px-3 py-2 space-y-2">
              <label className="block text-sm font-medium text-gray-700" htmlFor="planbaarheid-reden-input">
                Reden (verplicht)
              </label>
              <textarea
                id="planbaarheid-reden-input"
                data-testid="planbaarheid-reden-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                maxLength={255}
                className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#54949E]"
                placeholder="Bijvoorbeeld: detachering tot 1 oktober"
              />
              {!reason.trim() && (
                <p
                  data-testid="planbaarheid-reden-verplicht"
                  className="text-xs text-[#D64545]"
                >
                  Een reden is verplicht — zonder reden is opslaan niet mogelijk.
                </p>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  data-testid="planbaarheid-opslaan-button"
                  onClick={handleSave}
                  disabled={setOverride.isPending || !reason.trim()}
                  className="rounded-md bg-[#2F5A7A] px-3 py-2 text-sm font-medium text-white hover:bg-[#2F5A7A]/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Opslaan
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsFormOpen(false);
                    setReason('');
                  }}
                  className="text-sm text-gray-600 hover:underline"
                >
                  Annuleren
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              data-testid="planbaarheid-uitzetten-button"
              onClick={() => setIsFormOpen(true)}
              className="rounded-md border border-[#E2E8F0] px-3 py-2 text-sm font-medium text-[#2F5A7A] hover:bg-gray-50"
            >
              Handmatig niet-planbaar zetten
            </button>
          )}
        </div>
      )}
    </div>
  );
}
